import { WorkflowState } from '@compass/client/types/case_record';
import moment from 'moment';

import { KnipperCaseState, PqcDecision } from '@/constants/caseStates';

import { calculateCompletedSLA, calculateSLA } from './sla';

type SlaStatusKind =
  | 'not_started'
  | 'running_ok'
  | 'running_near_breach'
  | 'running_breached'
  | 'paused'
  | 'submitted_pending'
  | 'submitted_question'
  | 'submitted_missing_info'
  | 'acked'
  | 'completed_met'
  | 'completed_breached'
  | 'terminal_closed';

export interface SlaStatusInput {
  workflowLastStartedAt?: string;
  workflowDurationSeconds?: number;
  slaMinutes?: number;
  isBreached?: boolean;
  completedAt?: string;
  workflowState?: WorkflowState;
  caseState?: string;
  stateUpdatedAt?: string;
  decision?: string;
}

export interface SlaStatusComputed {
  kind: SlaStatusKind;
  /** Elapsed minutes from workflow start to the relevant state, or remaining for running. */
  primaryMinutes?: number;
  /** Percentage elapsed against SLA (used for near-breach detection). */
  percentageElapsed: number;
  /**
   * Whether the SLA is breached and the "SLA Breached" badge should render. True for the inherently
   * breached kinds (running_breached, completed_breached) and, for any non-terminal state, whenever
   * the backend `isBreached` flag is set — so intermediate states (submitted, acknowledged, paused)
   * surface a real breach. Terminal-closed and not_started never breach.
   */
  breached?: boolean;
}

export const TERMINAL_CLOSED_STATES = new Set<string>([
  KnipperCaseState.CANCELLED,
  KnipperCaseState.DUPLICATE,
  KnipperCaseState.NON_REPORTABLE,
  KnipperCaseState.FAILED,
  KnipperCaseState.TERMINATED,
]);

export const TERMINAL_VERBS: Record<string, string> = {
  [KnipperCaseState.CANCELLED]: 'Cancelled',
  [KnipperCaseState.DUPLICATE]: 'Marked Duplicate',
  [KnipperCaseState.NON_REPORTABLE]: 'Marked Non-reportable',
  [KnipperCaseState.FAILED]: 'Failed',
  [KnipperCaseState.TERMINATED]: 'Terminated',
};

/** PQC near-breach threshold — when percentageElapsed crosses this, status flips to running_near_breach. */
export const PQC_NEAR_BREACH_THRESHOLD = 75;

export function elapsedFromStartToState(
  workflowLastStartedAt: string | undefined,
  stateTimestamp: string | undefined,
): number | undefined {
  if (!workflowLastStartedAt) return undefined;
  const start = moment(workflowLastStartedAt);
  if (!start.isValid()) return undefined;
  const end = stateTimestamp ? moment(stateTimestamp) : moment();
  if (!end.isValid()) return undefined;
  return Math.max(0, end.diff(start, 'minutes'));
}

/**
 * Total workflow runtime in minutes for a terminal / submitted state.
 *
 * Prefers the backend's accumulated `workflow_duration_seconds` (runtime summed
 * across every workflow run) so the value stays correct even when the workflow
 * restarted. A restart resets `workflow_last_started_at`, so measuring from it
 * alone (via {@link elapsedFromStartToState}) would reflect only the final run
 * and under-report the elapsed SLA time. Falls back to the last-run wall clock
 * when the accumulated value is unavailable. Mirrors the elapsed-minutes logic
 * in `calculateCompletedSLA`.
 */
function totalRuntimeMinutes(
  input: SlaStatusInput,
  stateTimestamp: string | undefined,
): number | undefined {
  const { workflowDurationSeconds, workflowLastStartedAt } = input;
  if (workflowDurationSeconds !== undefined && workflowDurationSeconds > 0) {
    return Math.floor(workflowDurationSeconds / 60);
  }
  return elapsedFromStartToState(workflowLastStartedAt, stateTimestamp);
}

function classifySubmittedKind(decision: string | undefined): SlaStatusKind {
  if (decision === PqcDecision.QUESTION_ASKED) return 'submitted_question';
  if (decision === PqcDecision.MISSING_INFO) return 'submitted_missing_info';
  return 'submitted_pending';
}

function isAcknowledgedState(caseState: string | undefined, decision: string | undefined): boolean {
  return caseState === KnipperCaseState.ACKNOWLEDGED || decision === PqcDecision.ACKNOWLEDGED;
}

function isTerminalClosed(caseState: string | undefined): boolean {
  return !!caseState && TERMINAL_CLOSED_STATES.has(caseState);
}

function classifyCompleted(input: SlaStatusInput, completedAt: string): SlaStatusComputed {
  const { workflowLastStartedAt, workflowDurationSeconds, slaMinutes, isBreached } = input;
  const completed = calculateCompletedSLA(
    workflowLastStartedAt,
    completedAt,
    workflowDurationSeconds,
    slaMinutes,
  );
  const breached = completed.isBreached || isBreached;
  return {
    kind: breached ? 'completed_breached' : 'completed_met',
    primaryMinutes: totalRuntimeMinutes(input, completedAt),
    percentageElapsed: completed.percentageElapsed,
  };
}

function classifyRunning(input: SlaStatusInput): SlaStatusComputed {
  const running = calculateSLA(
    input.workflowLastStartedAt,
    input.workflowDurationSeconds,
    input.slaMinutes,
    input.isBreached,
    input.workflowState,
  );
  const common = {
    primaryMinutes: running.remainingMinutes,
    percentageElapsed: running.percentageElapsed,
  };
  if (running.isBreached) return { kind: 'running_breached', ...common };
  if (running.percentageElapsed >= PQC_NEAR_BREACH_THRESHOLD) {
    return { kind: 'running_near_breach', ...common };
  }
  return { kind: 'running_ok', ...common };
}

function computeSlaStatus(input: SlaStatusInput): SlaStatusComputed {
  const { workflowLastStartedAt, completedAt, workflowState, caseState, stateUpdatedAt, decision } =
    input;

  if (!workflowLastStartedAt) {
    return { kind: 'not_started', percentageElapsed: 0 };
  }

  if (workflowState === WorkflowState.WORKFLOW_STATE_PAUSED) {
    const running = classifyRunning(input);
    return { ...running, kind: 'paused' };
  }

  // PQC ACKNOWLEDGED is a Knipper terminal state that renders as ack-received (not closed).
  if (isAcknowledgedState(caseState, decision)) {
    return {
      kind: 'acked',
      primaryMinutes: totalRuntimeMinutes(input, stateUpdatedAt),
      percentageElapsed: 0,
    };
  }

  if (caseState === KnipperCaseState.SUBMITTED) {
    return {
      kind: classifySubmittedKind(decision),
      primaryMinutes: totalRuntimeMinutes(input, stateUpdatedAt),
      percentageElapsed: 0,
    };
  }

  if (isTerminalClosed(caseState)) {
    return {
      kind: 'terminal_closed',
      primaryMinutes: totalRuntimeMinutes(input, stateUpdatedAt || completedAt),
      percentageElapsed: 0,
    };
  }

  if (completedAt) {
    return classifyCompleted(input, completedAt);
  }

  return classifyRunning(input);
}

/**
 * Pure state-machine for the PQC SLA Status column. Extracted from SLATimer for testability.
 * Wraps {@link computeSlaStatus} to also derive `breached`, so the backend `isBreached` flag is
 * honoured for every non-terminal state (submitted/acknowledged/paused) — not only the running and
 * completed kinds where breach is time-derived.
 */
export function determineSlaStatus(input: SlaStatusInput): SlaStatusComputed {
  const computed = computeSlaStatus(input);
  const breached =
    computed.kind === 'running_breached' ||
    computed.kind === 'completed_breached' ||
    (input.isBreached === true &&
      computed.kind !== 'terminal_closed' &&
      computed.kind !== 'not_started');
  return { ...computed, breached };
}
